import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Quote } from "lucide-react";

export function TrustSection() {
  const { t } = useTranslation();
  const testimonials = t("trust.testimonials", { returnObjects: true }) as Array<{
    quote: string;
    name: string;
    title: string;
    company: string;
    industry: string;
  }>;

  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isVisible, setIsVisible] = useState(true);

  const next = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
      setIsVisible(true);
    }, 200);
  }, [testimonials.length]);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(next, 3000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, next]);

  const handleDotClick = (index: number) => {
    if (index === current) return;
    setIsAutoPlaying(false);
    setIsVisible(false);
    setTimeout(() => {
      setCurrent(index);
      setIsVisible(true);
    }, 200);
  };

  return (
    <section className="py-24 md:py-32 bg-background">
      <div className="container">
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-rows-[auto_1fr_auto_auto_auto] text-center min-h-[320px]">
            <div className="flex justify-center mb-8">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Quote className="h-4 w-4 text-primary" />
              </div>
            </div>

            <div className="flex items-center justify-center min-h-[120px] sm:h-[140px] mb-8">
              <blockquote 
                className={`text-xl sm:text-2xl md:text-3xl font-medium text-foreground leading-relaxed transition-opacity duration-200 ${
                  isVisible ? "opacity-100" : "opacity-0"
                }`}
              >
                "{testimonials[current].quote}"
              </blockquote>
            </div>

            <div 
              className={`h-[80px] transition-opacity duration-200 ${
                isVisible ? "opacity-100" : "opacity-0"
              }`}
            >
              <div className="font-semibold text-foreground">
                {testimonials[current].name}
              </div>
              <div className="text-muted-foreground text-sm mb-3">
                {testimonials[current].title}, {testimonials[current].company}
              </div>
              <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                {testimonials[current].industry}
              </span>
            </div>

            <div className="flex items-center justify-center gap-6 pt-6">
              <div className="flex items-center gap-2">
                {/* Each button is a 24×24 px hit area (WCAG 2.5.8 target size,
                    CWS-208); the visible dot is the inner span, so the 32 px
                    active pill overflows its button box by design (`shrink-0`
                    keeps flexbox from collapsing it to the box width) and the
                    hover highlight lives on the box, not the paint. */}
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className="group flex h-6 w-6 items-center justify-center"
                    aria-label={`Go to testimonial ${index + 1}`}
                  >
                    <span
                      className={`h-1.5 shrink-0 rounded-full transition-all duration-300 ${
                        index === current
                          ? "w-8 bg-primary"
                          : "w-1.5 bg-border group-hover:bg-primary/40"
                      }`}
                    />
                  </button>
                ))}
              </div>

              <div className="h-4 w-px bg-border" />

              <a 
                href="https://clutch.co/profile/shelpuk-ai-technology-consulting" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="text-primary">★</span>
                <span>5.0 on Clutch</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
